<template>
    <v-row class="ma-0">
        <v-col cols="12" >
            <v-text-field
                ref="decisionAtInput"
                v-model="decisionAt"
                label="Recusado a"
                :clearable="!readonly"
                type="datetime-local"
                :min="normalizedMinDateTime"
                :disabled="isSubmitting || disabled"
                :readonly="readonly"
                :rules="[validationRules.required(decisionAt)]"
            ></v-text-field>
        </v-col>
        <v-col cols="12" >
            <v-autocomplete
                ref="decisionByUserInput"
                v-model="decisionByUserId"
                label="Recusado por"
                :items="decisionUsersList"
                item-title="name"
                item-value="id"
                :disabled="isSubmitting || isLoadingUsersResolved || !decisionAt || disabled"
                :loading="isLoadingUsersResolved"
                :clearable="!readonly"
                :readonly="readonly"
                autocomplete="off"
                :rules="[validationRules.required(decisionByUserId)]"
                :menu-icon="readonly ? '' : undefined"
            ></v-autocomplete>
        </v-col>
    </v-row>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import dayjs from "dayjs";
import validationRules from "@/composables/formValidationRules"; 


const props = defineProps({
    deniedAt: {
        type: String,
        default: null,
    },
    deniedByUserId: {
        type: String,
        default: null,
    },
    departmentId: {
        type: String,
        default: null,
    },
    departmentType: {
        type: String,
        default: "initial",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    isLoadingUsers: {
        type: Boolean,
        default: false,
    },
    minDateTime: {
        type: String,
        default: null,
    },
    onSubmit: {
        type: Function,
        default: null,
    },
    users: {
        type: Array,
        default: null,
    },
    isSubmitting: {
        type: Boolean,
        default: false,
    }
});

const emit = defineEmits([
    "update:deniedAt",
    "update:deniedByUserId",
]);

defineExpose({
    submit,
    validate,
});

// const isSubmitting = ref(false);
const decisionAtInput = ref(null);
const decisionByUserInput = ref(null);


const localDeniedAt = ref(props.deniedAt);
const localDeniedByUserId = ref(props.deniedByUserId);
const localIsSubmitting = ref(props.isSubmitting);

watch(() => props.deniedAt, (value) => {
    localDeniedAt.value = value;
});

watch(() => props.deniedByUserId, (value) => {
    localDeniedByUserId.value = value;
});

watch(() => props.isSubmitting, (value) => {
    localIsSubmitting.value = value;
})

const decisionAt = computed({
    get() {
        return localDeniedAt.value;
    },
    set(value) {
        localDeniedAt.value = value;
        emit("update:deniedAt", value);
    },
});

const decisionByUserId = computed({
    get() {
        return localDeniedByUserId.value;
    },
    set(value) {
        localDeniedByUserId.value = value;
        emit("update:deniedByUserId", value);
    },
});


const decisionUsersList = computed(() => {
    return props.users ?? [];
});

const isLoadingUsersResolved = computed(() => {
    return props.isLoadingUsers;
});

const normalizedMinDateTime = computed(() => {
    return formatDateTimeLocal(props.minDateTime);
});

onMounted(() => {
    if (!decisionAt.value) {
        decisionAt.value = formatDateTimeLocal(new Date());
    }

});

function formatDateTimeLocal(value) {
    return value
        ? dayjs(value).format("YYYY-MM-DDTHH:mm")
        : null;
}

function normalizeDateTime(value) {
    return value
        ? dayjs(value).format("YYYY-MM-DD HH:mm:ss")
        : null;
}

async function validate() {
    const results = await Promise.all([
        decisionAtInput.value?.validate(),
        decisionByUserInput.value?.validate(),
    ]);

    return results.every((result) => {
        if (Array.isArray(result)) {
            return result.length === 0;
        }

        return result === true || result?.valid === true;
    });
}


async function submit() {
    props.isSubmitting.value = true;

    try {
        const passedValidation = await validate();

        if (!passedValidation) {
            return false;
        }

        const payload = {
            department_type: props.departmentType,
            denied_at: normalizeDateTime(decisionAt.value),
            denied_by_user_id: decisionByUserId.value,
        };

        if (props.onSubmit) {
            await props.onSubmit(payload);
        }

        return payload;
    } finally {
        props.isSubmitting.value = false;
    }
}

</script>
